/**
 * Foreground re-probe (mobile PWA bug): when the tab returns to the foreground,
 * the browser may have throttled the health-check interval and an in-flight ping
 * may have failed, leaving the API marked "down". No `online` event fires because
 * the network never changed, so nothing else forces a re-check. This listener
 * re-syncs the network state and forces an immediate API probe.
 *
 * Targets are injected so the wiring can be tested without touching globals
 * (`apps/web/src/providers/__tests__/foreground-reprobe.test.ts`). The probe's
 * own recovery (down -> up) is covered in
 * `packages/shared/src/powersync/__tests__/manager.test.ts`.
 */
export type ForegroundReprobeTargets = {
  doc: Pick<EventTarget, "addEventListener" | "removeEventListener"> & {
    visibilityState: DocumentVisibilityState;
  };
  win: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  isOnline: () => boolean;
};

export type ForegroundReprobeManager = {
  handlers: { onNetworkChanged: (online: boolean) => void };
  forceHealthCheck: () => unknown;
};

/** Returns a detach function that removes all three listeners. */
export function attachForegroundReprobe(
  targets: ForegroundReprobeTargets,
  manager: ForegroundReprobeManager,
): () => void {
  const handleForeground = () => {
    if (targets.doc.visibilityState !== "visible") {
      return;
    }
    manager.handlers.onNetworkChanged(targets.isOnline());
    void manager.forceHealthCheck();
  };

  targets.doc.addEventListener("visibilitychange", handleForeground);
  targets.win.addEventListener("pageshow", handleForeground);
  targets.win.addEventListener("focus", handleForeground);

  return () => {
    targets.doc.removeEventListener("visibilitychange", handleForeground);
    targets.win.removeEventListener("pageshow", handleForeground);
    targets.win.removeEventListener("focus", handleForeground);
  };
}
